/* 在實際 App 上提供「畫面標記回饋」。回饋會以 step_key=對應報告區塊 寫入後端，
 * 因此會顯示在測試報告 /reports/yfeng-full-test-20260624.html 的對應功能區塊裡。
 *
 * 與報告端的回饋層共用同一後端（/report/*）與同一身分/白名單機制（LINE 登入）。
 * 設計成 vanilla 單例 init，由 <FeedbackWidget/> 在 AdminLayout 掛載一次。
 */
import liff from '@line/liff';

const API = '/api';
const LIFF_ID = (import.meta as any).env?.VITE_LIFF_ID || '';

type MarkerType = { k: string; label: string; color: string };
const TYPES: MarkerType[] = [
  { k: 'bug', label: 'BUG', color: '#f43f5e' },
  { k: 'improve', label: '改善', color: '#f59e0b' },
  { k: 'add', label: '新增', color: '#10b981' },
  { k: 'move', label: '移位', color: '#0ea5e9' },
  { k: 'question', label: '疑問', color: '#a855f7' },
];

// App 路由 → 測試報告區塊 id（回饋記到報告的「對應功能」）。
const ROUTE_MAP: { test: (p: string) => boolean; section: string; title: string }[] = [
  { test: (p) => p === '/' , section: 'wf-dashboard', title: 'Dashboard 主控台' },
  { test: (p) => p === '/projects/new', section: 'wf-create-project', title: '新增專案' },
  { test: (p) => /^\/projects\/[^/]+\/edit$/.test(p), section: 'wf-quote-editor', title: '報價編輯器' },
  { test: (p) => /^\/projects\/[^/]+$/.test(p), section: 'wf-projects', title: '專案詳細' },
  { test: (p) => p === '/projects', section: 'wf-projects', title: '專案列表' },
  { test: (p) => p === '/payments', section: 'wf-payments', title: '金流付款' },
  { test: (p) => p === '/payroll', section: 'wf-payroll', title: '薪資彙整' },
  { test: (p) => p === '/organizers', section: 'wf-projects', title: '整理師列表' },
  { test: (p) => p === '/settings', section: 'wf-settings', title: '系統設定' },
  { test: (p) => p.startsWith('/feedback'), section: 'wf-settings', title: '回饋處理' },
];
function routeToSection(p: string) {
  const m = ROUTE_MAP.find((r) => r.test(p));
  return m || { section: 'wf-dashboard', title: p };
}

interface Identity { user_id: string; name: string | null; is_admin: boolean; can_feedback: boolean }
// 一個標記：單點(w/h=0) 或 矩形框(w/h>0)。x/y/w/h 皆為「相對文件」比例 0..1。
type Mark = { marker_type: string; x_ratio: number; y_ratio: number; w_ratio: number; h_ratio: number; element_selector: string | null; element_text: string | null; label: string; relX?: number; relY?: number; relW?: number; relH?: number };

let mounted = false;

export function initAppFeedback() {
  if (mounted || typeof document === 'undefined') return;
  mounted = true;

  let auth: any = null;
  let identity: Identity | null = null;
  let selType = 'bug';
  let draft: { markers: Mark[]; screenshot: string | null; docW: number; docH: number } | null = null;

  const qs = new URLSearchParams(location.search);

  // ---------- styles ----------
  const style = document.createElement('style');
  style.textContent = `
    .afb-fab{position:fixed;right:22px;bottom:22px;z-index:9998;background:#1f6feb;color:#fff;border:0;border-radius:24px;padding:10px 16px;font-size:14px;font-weight:700;cursor:pointer;box-shadow:0 6px 18px rgba(0,0,0,.25)}
    .afb-fab:hover{filter:brightness(1.08)}
    .afb-panel{position:fixed;top:0;right:-420px;width:400px;max-width:94vw;height:100vh;background:#11161c;color:#e6edf3;border-left:1px solid #2c3742;z-index:10001;display:flex;flex-direction:column;transition:right .22s;box-shadow:-8px 0 24px rgba(0,0,0,.45)}
    .afb-panel.open{right:0}
    .afb-head{display:flex;justify-content:space-between;align-items:flex-start;padding:14px 16px;border-bottom:1px solid #2c3742;font-weight:700;font-size:14px}
    .afb-x{background:0;border:0;color:#9aa7b4;font-size:18px;cursor:pointer}
    .afb-body{flex:1;overflow:auto;padding:14px;display:flex;flex-direction:column;gap:10px}
    .afb-hint{font-size:12px;color:#8b97a3;line-height:1.6}
    .afb-row{display:flex;gap:6px;flex-wrap:wrap}
    .afb-type{font-size:12px;font-weight:700;border-radius:14px;padding:3px 10px;cursor:pointer;color:#0b0f14;opacity:.55;border:2px solid transparent}
    .afb-type.sel{opacity:1;box-shadow:0 0 0 2px #fff3}
    .afb-mark{background:#1f6feb;color:#fff;border:0;border-radius:8px;padding:9px 12px;font-weight:700;cursor:pointer}
    .afb-in{width:100%;background:#0f1419;border:1px solid #2c3742;border-radius:8px;color:#e6edf3;font-size:13px;padding:8px;box-sizing:border-box;resize:vertical}
    .afb-send{background:#10b981;color:#06281c;border:0;border-radius:8px;padding:9px 12px;font-weight:700;cursor:pointer}
    .afb-thumb{max-width:100%;border:1px solid #2c3742;border-radius:8px}
    .afb-login{background:#06C755;color:#fff;border:0;border-radius:8px;padding:9px 12px;font-weight:700;cursor:pointer}
    .afb-uid{font-size:10px;color:#6b7682;word-break:break-all}
    body.afb-marking{cursor:crosshair!important}
    .afb-banner{position:fixed;top:0;left:0;right:0;z-index:10002;background:#1f6feb;color:#fff;font-weight:700;padding:9px;display:flex;gap:8px;justify-content:center;align-items:center;flex-wrap:wrap;font-size:13px}
    .afb-banner select{border-radius:8px;padding:3px 8px;font-weight:700;color:#111827;background:#fff;border:0}
    .afb-banner button{border-radius:12px;padding:4px 12px;font-weight:800;cursor:pointer;border:0}
    .afb-modebtn{background:#ffffff22;border:1px solid #ffffff77!important;color:#fff;border-radius:10px;padding:3px 9px;cursor:pointer;font-weight:700}
    .afb-modebtn.sel{background:#fff;color:#1f6feb}
    .afb-toolbtn{background:#ffffff22;border:1px solid #ffffff55!important;color:#fff}
    .afb-pin{position:absolute;z-index:10001;width:22px;height:22px;border-radius:50% 50% 50% 0;transform:translate(-50%,-100%) rotate(-45deg);border:2px solid #fff;display:flex;align-items:center;justify-content:center;cursor:pointer}
    .afb-pin span{transform:rotate(45deg);color:#0b0f14;font-size:11px;font-weight:800}
    .afb-box{position:absolute;z-index:10001;border:2.5px solid;background:rgba(255,255,255,.06);box-sizing:border-box;cursor:pointer;border-radius:3px}
    .afb-mini-box{position:absolute;border:2px solid;box-sizing:border-box;border-radius:2px}
    .afb-toast{position:fixed;left:50%;bottom:80px;transform:translateX(-50%);z-index:10010;background:#0f3d22;color:#5ad97b;border:1px solid #2ea04355;border-radius:8px;padding:10px 16px;font-size:13px;font-weight:700;box-shadow:0 6px 18px rgba(0,0,0,.3)}
  `;
  document.head.appendChild(style);

  const pinLayer = document.createElement('div');
  pinLayer.className = 'afb-pinlayer';
  pinLayer.style.cssText = 'position:absolute;top:0;left:0;z-index:10001';
  document.body.appendChild(pinLayer);

  const fab = document.createElement('button');
  fab.className = 'afb-fab';
  fab.textContent = '💬 回饋';
  document.body.appendChild(fab);

  const panel = document.createElement('div');
  panel.className = 'afb-panel';
  document.body.appendChild(panel);

  function toast(msg: string) {
    const t = document.createElement('div');
    t.className = 'afb-toast';
    t.textContent = msg;
    document.body.appendChild(t);
    setTimeout(() => t.remove(), 3200);
  }
  function dims() {
    const b = document.body, e = document.documentElement;
    return { w: Math.max(b.scrollWidth, e.scrollWidth, e.clientWidth), h: Math.max(b.scrollHeight, e.scrollHeight, e.clientHeight) };
  }
  function buildSelector(el: Element | null): string | null {
    if (!el || el === document.body) return 'body';
    const parts: string[] = []; let n: Element | null = el; let d = 0;
    while (n && n.nodeType === 1 && n !== document.body && d < 5) {
      let s = n.tagName.toLowerCase();
      if ((n as HTMLElement).id) { parts.unshift('#' + CSS.escape((n as HTMLElement).id)); break; }
      const cls = (n.getAttribute('class') || '').trim().split(/\s+/).filter(Boolean).slice(0, 2);
      if (cls.length) s += '.' + cls.map((x) => CSS.escape(x)).join('.');
      const p: Element | null = n.parentElement;
      if (p) { const sib = Array.from(p.children).filter((c) => c.tagName === n!.tagName); if (sib.length > 1) s += `:nth-of-type(${sib.indexOf(n) + 1})`; }
      parts.unshift(s); n = p; d++;
    }
    return parts.join(' > ');
  }

  async function session(a: any): Promise<Identity | null> {
    if (!a) return null;
    try { const r = await fetch(`${API}/report/session`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(a) }); return r.ok ? await r.json() : null; } catch { return null; }
  }
  // LIFF 只 init 一次（重複 init 在 @line/liff 會 reject「already initialized」，
  // 之前被 silent catch 吞掉導致按登入沒反應）。
  let liffReadyP: Promise<boolean> | null = null;
  function ensureLiff(): Promise<boolean> {
    if (!LIFF_ID) return Promise.resolve(false);
    if (!liffReadyP) liffReadyP = liff.init({ liffId: LIFF_ID }).then(() => true).catch(() => false);
    return liffReadyP;
  }
  async function resolveIdentity() {
    const dev = qs.get('dev_user_id');
    if (dev) { auth = { dev_user_id: dev, dev_name: qs.get('dev_name') || undefined }; identity = await session(auth); return; }
    if ((await ensureLiff()) && liff.isLoggedIn()) {
      const idt = liff.getIDToken();
      if (idt) { auth = { id_token: idt }; identity = await session(auth); }
    }
  }
  async function login() {
    const dev = qs.get('dev_user_id');
    if (dev) { await resolveIdentity(); drawPanel(); return; }
    if (!LIFF_ID) { toast('未設定 LINE 登入（LIFF ID）'); return; }
    if (!(await ensureLiff())) { toast('LINE 登入初始化失敗，請重試'); return; }
    if (!liff.isLoggedIn()) {
      // LIFF 登入會導回 endpoint(/liff/quote)；存返回位址讓 LiffQuotePage 把我們彈回原頁。
      try { sessionStorage.setItem('liffReturnTo', location.pathname + location.search + location.hash); } catch { /* ignore */ }
      liff.login();
      return;
    }
    const idt = liff.getIDToken();
    if (idt) { auth = { id_token: idt }; identity = await session(auth); drawPanel(); }
    else { toast('取得 LINE 身分失敗，請重新整理'); }
  }

  function openPanel() { draft = null; drawPanel(); panel.classList.add('open'); }
  function closePanel() { panel.classList.remove('open'); }

  function drawPanel() {
    const map = routeToSection(location.pathname);
    let foot = '';
    if (!identity) {
      foot = `<div class="afb-hint">回饋需透過 LINE 登入（與測試報告同一機制）。</div><button class="afb-login">用 LINE 登入</button>`;
    } else if (!identity.can_feedback) {
      foot = `<div class="afb-hint" style="color:#e3b341">您目前不在回饋名單，無法新增回饋。請把下方 LINE ID 提供給管理員加入名單。</div><div class="afb-uid">${identity.user_id}</div>`;
    } else if (!draft) {
      foot = `<div class="afb-hint">在「${map.title}」頁面留下回饋，會記錄到測試報告的對應功能區塊。</div>
        <div class="afb-row">${TYPES.map((t, i) => `<span class="afb-type${i === 0 ? ' sel' : ''}" data-k="${t.k}" style="background:${t.color}">${t.label}</span>`).join('')}</div>
        <button class="afb-mark">📍 標記畫面</button>
        <textarea class="afb-in" rows="3" placeholder="描述這個問題/建議…（必填）"></textarea>
        <button class="afb-send">送出回饋</button>`;
    } else {
      const pins = draft.markers.map((m) => {
        const t = TYPES.find((x) => x.k === m.marker_type) || TYPES[0];
        if ((m.relW || 0) > 0 || (m.relH || 0) > 0) {
          return `<div class="afb-mini-box" style="border-color:${t.color};left:${(m.relX || 0) * 100}%;top:${(m.relY || 0) * 100}%;width:${(m.relW || 0) * 100}%;height:${(m.relH || 0) * 100}%"></div>`;
        }
        return `<div class="afb-pin" style="position:absolute;background:${t.color};left:${(m.relX || 0) * 100}%;top:${(m.relY || 0) * 100}%"><span>${t.label.slice(0, 1)}</span></div>`;
      }).join('');
      foot = `<div class="afb-hint">已標記 ${draft.markers.length} 處。填描述後送出。</div>
        ${draft.screenshot ? `<div style="position:relative"><img class="afb-thumb" src="${draft.screenshot}"/>${pins}</div>` : ''}
        <textarea class="afb-in" rows="3" placeholder="描述這個問題/建議…（必填）"></textarea>
        <div class="afb-row"><button class="afb-mark" style="flex:1;background:#33404d">↺ 重新標記</button><button class="afb-send" style="flex:1">送出回饋</button></div>`;
    }
    panel.innerHTML = `<div class="afb-head"><div>💬 回饋 · ${map.title}</div><button class="afb-x">✕</button></div><div class="afb-body">${foot}</div>`;
    panel.querySelector('.afb-x')!.addEventListener('click', closePanel);
    panel.querySelector('.afb-login')?.addEventListener('click', login);
    panel.querySelectorAll('.afb-type').forEach((b) => b.addEventListener('click', () => {
      selType = (b as HTMLElement).dataset.k!;
      panel.querySelectorAll('.afb-type').forEach((x) => x.classList.remove('sel'));
      b.classList.add('sel');
    }));
    panel.querySelector('.afb-mark')?.addEventListener('click', startMarking);
    panel.querySelector('.afb-send')?.addEventListener('click', submit);
  }

  // ---------- marking (點 + 框 + 刪除) ----------
  type SessMark = { marker_type: string; xpx: number; ypx: number; wpx: number; hpx: number; sel: string | null; txt: string | null; node: HTMLElement };
  let marking: { shape: 'point' | 'box'; type: string; marks: SessMark[]; banner: HTMLElement; drag: { x0: number; y0: number; node: HTMLElement; cur?: { x: number; y: number; w: number; h: number } } | null } | null = null;

  function startMarking() {
    panel.classList.remove('open');
    const banner = document.createElement('div');
    banner.className = 'afb-banner';
    banner.innerHTML =
      `📍 標記（可多個・點標記可刪）　` +
      `<select class="afb-sel">${TYPES.map((t) => `<option value="${t.k}">${t.label}</option>`).join('')}</select>　` +
      `<button class="afb-modebtn sel" data-shape="point">📍 點</button><button class="afb-modebtn" data-shape="box">▭ 框</button>　` +
      `<button class="afb-toolbtn afb-undo">↶ 復原</button><button class="afb-toolbtn afb-clear">清空</button>　` +
      `<button class="afb-done" style="background:#fff;color:#1f6feb">完成</button>` +
      `<button class="afb-cancel" style="background:transparent;border:1px solid #ffffff88;color:#fff">取消</button>`;
    document.body.appendChild(banner);
    document.body.classList.add('afb-marking');
    const sel = banner.querySelector('.afb-sel') as HTMLSelectElement; sel.value = selType;
    marking = { shape: 'point', type: sel.value, marks: [], banner, drag: null };
    sel.addEventListener('change', () => { if (marking) marking.type = sel.value; });
    banner.querySelectorAll('.afb-modebtn').forEach((b) => b.addEventListener('click', () => {
      if (!marking) return; marking.shape = (b as HTMLElement).dataset.shape as 'point' | 'box';
      banner.querySelectorAll('.afb-modebtn').forEach((x) => x.classList.remove('sel')); b.classList.add('sel');
    }));
    banner.querySelector('.afb-undo')!.addEventListener('click', () => removeMark(marking!.marks.length - 1));
    banner.querySelector('.afb-clear')!.addEventListener('click', () => { while (marking && marking.marks.length) removeMark(marking.marks.length - 1); });
    banner.querySelector('.afb-done')!.addEventListener('click', finishMarking);
    banner.querySelector('.afb-cancel')!.addEventListener('click', cancelMarking);
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('pointermove', onMove, true);
    document.addEventListener('pointerup', onUp, true);
  }
  function colorOf(k: string) { return (TYPES.find((x) => x.k === k) || TYPES[0]).color; }
  function labelOf(k: string) { return (TYPES.find((x) => x.k === k) || TYPES[0]).label; }
  function makePin(x: number, y: number, k: string): HTMLElement {
    const pin = document.createElement('div'); pin.className = 'afb-pin'; pin.style.background = colorOf(k); pin.style.left = x + 'px'; pin.style.top = y + 'px';
    pin.innerHTML = `<span>${labelOf(k).slice(0, 1)}</span>`; pinLayer.appendChild(pin); return pin;
  }
  function makeBox(x: number, y: number, w: number, h: number, k: string): HTMLElement {
    const b = document.createElement('div'); b.className = 'afb-box'; b.style.borderColor = colorOf(k); setBox(b, x, y, w, h); pinLayer.appendChild(b); return b;
  }
  function setBox(b: HTMLElement, x: number, y: number, w: number, h: number) { b.style.left = x + 'px'; b.style.top = y + 'px'; b.style.width = w + 'px'; b.style.height = h + 'px'; }
  function hitIndex(target: EventTarget | null): number {
    if (!marking || !(target instanceof Node)) return -1;
    return marking.marks.findIndex((m) => m.node === target || m.node.contains(target as Node));
  }
  function removeMark(i: number) { if (!marking || i < 0 || i >= marking.marks.length) return; marking.marks[i].node.remove(); marking.marks.splice(i, 1); }

  function onDown(ev: PointerEvent) {
    if (!marking) return;
    if ((ev.target as Element).closest?.('.afb-banner')) return;
    ev.preventDefault(); ev.stopPropagation();
    const hit = hitIndex(ev.target);
    if (hit >= 0) { removeMark(hit); return; }       // 點到既有標記 → 刪除
    if (marking.shape === 'point') {
      const el = document.elementFromPoint(ev.clientX, ev.clientY);
      const node = makePin(ev.pageX, ev.pageY, marking.type);
      marking.marks.push({ marker_type: marking.type, xpx: ev.pageX, ypx: ev.pageY, wpx: 0, hpx: 0, sel: buildSelector(el), txt: el ? (el as HTMLElement).innerText?.trim().slice(0, 120) || null : null, node });
      return;
    }
    // box: 開始拖曳
    marking.drag = { x0: ev.pageX, y0: ev.pageY, node: makeBox(ev.pageX, ev.pageY, 0, 0, marking.type) };
  }
  function onMove(ev: PointerEvent) {
    if (!marking || !marking.drag) return;
    const { x0, y0 } = marking.drag;
    const x = Math.min(x0, ev.pageX), y = Math.min(y0, ev.pageY), w = Math.abs(ev.pageX - x0), h = Math.abs(ev.pageY - y0);
    setBox(marking.drag.node, x, y, w, h); marking.drag.cur = { x, y, w, h };
  }
  function onUp() {
    if (!marking || !marking.drag) return;
    const cur = marking.drag.cur, node = marking.drag.node; marking.drag = null;
    if (!cur || cur.w < 8 || cur.h < 8) { node.remove(); return; } // 太小視為誤觸
    const el = document.elementFromPoint(cur.x + cur.w / 2 - window.scrollX, cur.y + cur.h / 2 - window.scrollY);
    marking.marks.push({ marker_type: marking.type, xpx: cur.x, ypx: cur.y, wpx: cur.w, hpx: cur.h, sel: buildSelector(el), txt: el ? (el as HTMLElement).innerText?.trim().slice(0, 120) || null : null, node });
  }
  function teardownMarking() {
    document.removeEventListener('pointerdown', onDown, true);
    document.removeEventListener('pointermove', onMove, true);
    document.removeEventListener('pointerup', onUp, true);
    document.body.classList.remove('afb-marking');
    if (marking) { marking.marks.forEach((m) => m.node.remove()); if (marking.drag) marking.drag.node.remove(); marking.banner.remove(); }
  }
  function cancelMarking() { teardownMarking(); marking = null; panel.classList.add('open'); }
  function withTimeout<T>(p: Promise<T>, ms: number): Promise<T | null> { return Promise.race([p, new Promise<null>((r) => setTimeout(() => r(null), ms))]); }

  async function ensureLib(): Promise<boolean> {
    if ((window as any).modernScreenshot) return true;
    return new Promise((res) => { const s = document.createElement('script'); s.src = 'https://cdn.jsdelivr.net/npm/modern-screenshot@4.4.39/dist/index.js'; s.onload = () => res(!!(window as any).modernScreenshot); s.onerror = () => res(false); document.head.appendChild(s); });
  }
  async function capture(): Promise<{ url: string; ox: number; oy: number; w: number; h: number } | null> {
    if (!(await ensureLib())) return null;
    const ms = (window as any).modernScreenshot;
    const ox = window.scrollX, oy = window.scrollY, w = window.innerWidth, h = window.innerHeight;
    const hide = (n: any) => !(n.classList && (n.classList.contains('afb-fab') || n.classList.contains('afb-panel') || n.classList.contains('afb-banner') || n.classList.contains('afb-pinlayer') || n.classList.contains('afb-toast')));
    try {
      const url = await ms.domToJpeg(document.body, { quality: 0.9, backgroundColor: '#ffffff', width: w, height: h, style: { transform: `translate(${-ox}px,${-oy}px)`, transformOrigin: 'top left' }, filter: hide });
      return { url, ox, oy, w, h };
    } catch { return null; }
  }
  async function finishMarking() {
    const marks = marking!.marks.slice();
    const btn = marking!.banner.querySelector('.afb-done') as HTMLButtonElement; if (btn) { btn.textContent = '截圖中…'; btn.disabled = true; }
    const d = dims();
    const cap = await withTimeout(capture(), 20000);
    teardownMarking(); marking = null;
    const clamp = (v: number) => Math.min(1, Math.max(0, v));
    const rel: Mark[] = marks.map((m) => {
      let relX = 0.5, relY = 0.5, relW = m.wpx / d.w, relH = m.hpx / d.h;
      if (cap) { relX = (m.xpx - cap.ox) / cap.w; relY = (m.ypx - cap.oy) / cap.h; relW = m.wpx / cap.w; relH = m.hpx / cap.h; }
      return {
        marker_type: m.marker_type,
        x_ratio: clamp(m.xpx / d.w), y_ratio: clamp(m.ypx / d.h), w_ratio: clamp(m.wpx / d.w), h_ratio: clamp(m.hpx / d.h),
        element_selector: m.sel, element_text: m.txt, label: labelOf(m.marker_type),
        relX: clamp(relX), relY: clamp(relY), relW: clamp(relW), relH: clamp(relH),
      };
    });
    draft = { markers: rel, screenshot: cap ? cap.url : null, docW: d.w, docH: d.h };
    panel.classList.add('open'); drawPanel();
  }

  async function submit() {
    const ta = panel.querySelector('.afb-in') as HTMLTextAreaElement;
    const v = ta ? ta.value.trim() : '';
    if (!v) { toast('請填寫描述'); return; }
    if (!auth) { toast('請先登入'); return; }
    const map = routeToSection(location.pathname);
    const body: any = { ...auth, step_key: map.section, step_title: `[App] ${map.title}`, content: v, page_path: location.pathname + location.search, capture_mode: 'viewport' };
    if (draft) {
      body.markers = draft.markers.map((m) => ({ marker_type: m.marker_type, x_ratio: m.x_ratio, y_ratio: m.y_ratio, w_ratio: m.w_ratio, h_ratio: m.h_ratio, element_selector: m.element_selector, element_text: m.element_text, label: m.label }));
      body.screenshot = draft.screenshot || undefined; body.doc_width = draft.docW; body.doc_height = draft.docH;
    }
    const sb = panel.querySelector('.afb-send') as HTMLButtonElement; if (sb) { sb.textContent = '送出中…'; sb.disabled = true; }
    try {
      const r = await fetch(`${API}/report/comments`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (r.ok) { draft = null; closePanel(); toast('已記錄回饋，將顯示在測試報告的「' + map.title + '」'); }
      else if (r.status === 403) toast('您不在回饋名單，無法新增回饋');
      else toast('送出失敗 (' + r.status + ')');
    } catch { toast('送出失敗'); }
  }

  fab.addEventListener('click', () => { if (panel.classList.contains('open')) { closePanel(); } else { openPanel(); } });

  resolveIdentity();
}
